import { Skeleton } from '@chakra-ui/react';
import React, { createContext, useContext, useMemo } from 'react';

import { finleyAssert } from '@finley-technologies/common';

import { useGetCreditFacilitiesByOrganizationQuery } from '~/contexts/CreditFacilitiesProvider/requests/getCreditFacilities.gql.generated';

type SlugsToNames = {
  creditFacilities: Map<string, string>;
  loanProducts: Map<string, string>;
};

const SlugToNameContext = createContext<SlugsToNames | null>(null);

export const SlugToNameProvider = ({
  children,
  organizationId,
}: {
  children: React.ReactNode;
  organizationId: string;
}) => {
  const { data: cfData } = useGetCreditFacilitiesByOrganizationQuery({
    variables: { organizationId, creditFacilitySlugsToHide: [] },
  });

  const slugToNames: SlugsToNames | null = useMemo(() => {
    if (!cfData?.organization.creditFacilities) {
      return null;
    }
    const creditFacilities = new Map<string, string>();
    const loanProducts = new Map<string, string>();
    if (cfData?.organization.creditFacilities) {
      cfData.organization.creditFacilities.forEach((cf) => {
        creditFacilities.set(cf.slug, cf.name);
        cf.loanProducts.forEach((lp) => {
          loanProducts.set(lp.slug, lp.name);
        });
      });
    }
    return { creditFacilities, loanProducts };
  }, [cfData?.organization.creditFacilities]);

  if (!slugToNames) {
    return <Skeleton height="600px" data-testid="slugToNameProvider-loading" />;
  }

  return (
    <SlugToNameContext.Provider value={slugToNames}>
      {children}
    </SlugToNameContext.Provider>
  );
};

export const useSlugToName = () => {
  const context = useContext(SlugToNameContext);
  finleyAssert(
    context,
    'useSlugToName must be used within a SlugToNameProvider',
  );

  return useMemo(() => {
    return {
      cfSlugToName: (slug: string): string => {
        const name = context.creditFacilities.get(slug);
        finleyAssert(name, `Credit facility with [slug:${slug}] not found`);
        return name;
      },
      lpSlugToName: (slug: string): string => {
        const name = context.loanProducts.get(slug);
        finleyAssert(name, `Loan product with [slug:${slug}] not found`);
        return name;
      },
    };
  }, [context.creditFacilities, context.loanProducts]);
};
